/* Shared mini-UI primitives reused across the Phase 4 P1 admin pages. */
import { Loader2, Save, X, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import AdminMobileNav from "./AdminMobileNav";

export function FieldInput({ label, value, onChange, placeholder, testId, required, type = "text", className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}

export function FieldTextarea({ label, value, onChange, rows = 3, testId, className = "", placeholder }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40 font-sans" />
    </label>
  );
}

export function FieldSelect({ label, value, onChange, testId, options, className = "", emptyLabel = "—" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40">
        <option value="">{emptyLabel}</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

export function FieldToggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between bg-warmgray-50 border border-warmgray-200 rounded-xl px-3 py-2.5 cursor-pointer">
      <span className="text-xs font-medium text-warmgray-700">{label}</span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    </label>
  );
}

export function Toast({ message, testId = "shared-toast" }) {
  if (!message) return null;
  return <div data-testid={testId} className="mb-4 inline-block bg-[#E1EDE6] text-[#2D4A3E] rounded-xl px-4 py-2 text-sm font-medium">{message}</div>;
}

export function DialogShell({ title, onClose, onSubmit, saving, children, footerExtra, testIdPrefix, maxWidth = "max-w-2xl", saveLabel = "Lưu", cancelLabel = "Hủy", hideSubmit = false }) {
  return (
    <div className={`fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10`} data-testid={`${testIdPrefix}-dialog`}>
      <form onSubmit={onSubmit} className={`w-full ${maxWidth} bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] max-h-[92vh] flex flex-col`}>
        <div className="px-6 md:px-8 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl md:text-3xl text-charcoal">{title}</h3>
          <button type="button" onClick={onClose} data-testid={`${testIdPrefix}-dialog-close`} className="text-warmgray-500 hover:text-charcoal"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 md:px-8 py-6 overflow-y-auto flex-1">{children}</div>
        {!hideSubmit && (
          <div className="px-6 md:px-8 py-4 border-t border-warmgray-200 flex items-center justify-end gap-2">
            {footerExtra}
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal" data-testid={`${testIdPrefix}-dialog-cancel`}>{cancelLabel}</button>
            <button type="submit" disabled={saving} data-testid={`${testIdPrefix}-dialog-save`} className="inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {saveLabel}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export function ConfirmDialog({ title, body, onCancel, onConfirm, L = (vi) => vi }) {
  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/40 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6" data-testid="confirm-dialog">
        <h3 className="font-serif text-2xl text-charcoal">{title}</h3>
        <p className="mt-2 text-sm text-warmgray-700">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} data-testid="confirm-cancel" className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal">{L("Hủy", "Cancel")}</button>
          <button type="button" onClick={onConfirm} data-testid="confirm-confirm" className="bg-[#7A2E2E] text-white rounded-full px-4 py-2 text-sm font-medium hover:opacity-90">{L("Xóa", "Delete")}</button>
        </div>
      </div>
    </div>
  );
}

export function AdminPageHeader({ section, title, action, L, lang = "vi" }) {
  return (
    <header className="border-b border-espresso/10 bg-white/90 backdrop-blur-md sticky top-0 z-20">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-3.5 sm:py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Link to="/admin/nail-events" className="text-espresso-muted hover:text-espresso inline-flex items-center gap-1 text-sm shrink-0">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{L ? L("Quay lại", "Back") : "Quay lại"}</span>
          </Link>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-espresso-muted font-semibold truncate">{section}</p>
            <h1 className="font-serif text-lg sm:text-xl md:text-2xl text-espresso leading-none mt-1 truncate">{title}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {action}
          <AdminMobileNav lang={lang} />
        </div>
      </div>
    </header>
  );
}
